/**
 * /gbp-growth-agent inside the React app (WO-F8, Anand, 13 Oct 26). The landing is a static page now
 * (public/gbp-growth-agent/index.html, built from the design lab's final page by scripts/landing): a full page load
 * of the address is answered with that file, by Vercel's CDN or by server-landing.js, before React is involved. The
 * app still reaches its own /gbp-growth-agent route when a <Link to="/gbp-growth-agent"> is followed on a page that
 * is already the app. This route then loads the address for real, once, so the visitor lands on the static page.
 *
 * It replaces the history entry rather than adding one, so Back goes to the page the visitor came from, not to an
 * empty app page at the same address. It never loops: when this document itself was loaded at the landing's address,
 * the server answered with the app (the static file was missing or not served), so loading it again would only do
 * the same. The route then shows the React landing it was given, the page this address showed before.
 */
import { createElement, Fragment, useEffect, useState, type ReactNode } from 'react';

type HopWindow = Pick<Window, 'location' | 'performance' | 'sessionStorage'>;

const HOP_KEY = 'fly-landing-hop';
/** How long a hop is taken to be in flight, when the browser cannot say which address the document was loaded at. */
const HOP_WINDOW_MS = 10_000;

/** The navigation in flight from this document, so a second effect (React's StrictMode) does not start another. */
let inFlight: string | null = null;

const trim = (p: string) => (p.length > 1 ? p.replace(/\/+$/, '') : p);

/** The path this document was loaded at, from the navigation timing entry; null when the browser does not say. */
export function documentPath(w: HopWindow): string | null {
  try {
    const nav = w.performance?.getEntriesByType?.('navigation')?.[0] as PerformanceNavigationTiming | undefined;
    return nav?.name ? new URL(nav.name).pathname : null;
  } catch {
    return null;
  }
}

function recentHop(w: HopWindow, path: string): boolean {
  try {
    const [p, at] = (w.sessionStorage.getItem(HOP_KEY) ?? '').split('|');
    return p === path && Date.now() - Number(at) < HOP_WINDOW_MS;
  } catch {
    return false;
  }
}

/**
 * Loads the landing's address for real, once. 'hop': the navigation started. 'wait': one is in flight already.
 * 'stay': this document was loaded at this address (or, where the browser cannot say, a hop to it just happened),
 * so the app answered a full load and loading it again would loop.
 */
export function hopToStaticLanding(w: HopWindow): 'hop' | 'wait' | 'stay' {
  const path = trim(w.location.pathname);
  if (inFlight === path) return 'wait';
  const loaded = documentPath(w);
  if (loaded !== null ? trim(loaded) === path : recentHop(w, path)) return 'stay';
  inFlight = path;
  try {
    w.sessionStorage.setItem(HOP_KEY, `${path}|${Date.now()}`);
  } catch {
    /* private mode: the navigation timing entry still guards the loop */
  }
  w.location.replace(`${w.location.pathname}${w.location.search}${w.location.hash}`);
  return 'hop';
}

/** Test seam: forget the navigation in flight. */
export function resetStaticLandingHop(): void {
  inFlight = null;
}

/**
 * The /gbp-growth-agent route. Renders nothing (the server render too) and loads the static page; when that would
 * loop, renders `fallback`, the React landing.
 */
export default function StaticLandingHop({ fallback = null }: { fallback?: ReactNode }) {
  const [stay, setStay] = useState(false);
  useEffect(() => {
    if (hopToStaticLanding(window) === 'stay') setStay(true);
  }, []);
  return stay ? createElement(Fragment, null, fallback) : null;
}
